import { useCallback, useEffect, useState } from "react";

/**
 * Hook to find all elements with a given class name and update the state when the DOM changes
 *
 * @param className - The class name to search for
 * @param targetElement - The element to search within (default: document)
 * @returns An array of elements with the class name
 */
export const useClassElements = (className: string, targetElement: Element | Document = document) => {
  const [elements, setElements] = useState<Element[]>([]);

  // Function to find all elements with the class
  const findElements = useCallback(() => {
    // Convert HTMLCollection to Array for easier handling
    const elementsArray = Array.from(targetElement.getElementsByClassName(className));
    setElements(elementsArray);
  }, [className, targetElement]);

  useEffect(() => {
    // Initial check
    findElements();

    // Set up mutation observer to watch for DOM changes
    const observer = new MutationObserver((mutations) => {
      // Check for relevant changes before updating state
      const shouldUpdate = mutations.some((mutation) => {
        // Check for added/removed nodes
        if (mutation.type === "childList") {
          return true;
        }

        // Check for class attribute changes
        if (mutation.type === "attributes" && mutation.attributeName === "class") {
          return true;
        }

        return false;
      });

      if (shouldUpdate) {
        findElements();
      }
    });

    // Start observing with configuration
    observer.observe(targetElement, {
      childList: true, // Watch for changes to the direct children
      subtree: true, // Watch all descendants
      attributes: true, // Watch for attribute changes
      attributeFilter: ["class"], // Only care about class attribute changes
    });

    // Clean up the observer on component unmount
    return () => {
      observer.disconnect();
    };
  }, [className, targetElement, findElements]);

  return elements;
};
